import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { CLAIM } from '@constants/index'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { createGenericFetchError, getErrorMessage, isFetchBaseError } from '@utils/errorUtil'
import { pollClaimRecordController } from './pollClaimRecordController'

type ReadyCallback = (claimRecord: ClaimRecord) => boolean
type HandleResultCallback = (claimRecord: ClaimRecord, isTimeExceeded?: boolean) => void
type ErrorCallback = (error: FetchBaseQueryError) => void
type PollFunctionOptions = {
  claimRecordId?: string
}

type PollClaimRecordFunction = (
  readyCallback: ReadyCallback,
  handleResultCallback?: HandleResultCallback | null,
  errorCallback?: ErrorCallback | null,
  options?: PollFunctionOptions
) => void

type UsePollClaimRecordReturn = {
  pollClaimRecord: PollClaimRecordFunction
  isPolling: boolean
}

export const usePollClaimRecord = (
  maxTime: number,
  pollingInterval = CLAIM.POLLING_INTERVAL_MS
): UsePollClaimRecordReturn => {
  const [isPolling, setIsPolling] = useState(false)
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()

  const controller = useMemo(() => {
    const service = {
      fetchClaimRecord: async (id: string) => await fetchClaimRecord(id).unwrap(),
    }

    return pollClaimRecordController(service, { maxWaitTime: maxTime, pollingInterval })
  }, [fetchClaimRecord, maxTime, pollingInterval])

  const pollClaimRecord = useCallback(
    (
      readyCallback: ReadyCallback,
      handleResultCallback?: HandleResultCallback | null,
      errorCallback?: ErrorCallback | null,
      options?: PollFunctionOptions
    ) => {
      setIsPolling(true)

      const handlers = {
        isReady: (claimRecord: ClaimRecord) => readyCallback(claimRecord),
        onReady: (claimRecord: ClaimRecord) => {
          setIsPolling(false)
          handleResultCallback?.(claimRecord)
        },
        onError: (error: unknown) => {
          setIsPolling(false)
          if (isFetchBaseError(error)) {
            errorCallback?.(error)
          } else {
            errorCallback?.(createGenericFetchError(getErrorMessage(error) ?? 'Poll claim record error'))
          }
        },
        onTimeExceeded: (claimRecord: ClaimRecord) => {
          setIsPolling(false)
          handleResultCallback?.(claimRecord, true)
        },
      }

      controller.start(handlers, options?.claimRecordId ?? claimRecordId)
    },
    [controller, claimRecordId]
  )

  // stop polling on unmount
  useEffect(() => {
    return () => {
      controller.stop()
    }
  }, [controller])

  return { pollClaimRecord, isPolling }
}
